/**
 * Create Order - Multi-Step Wizard
 * 4 steps: Basic Info → Locations → Products → Review
 * Brand-teal industrial aesthetic with live preview panel.
 *
 * TypeScript since ST-00227 (was index.js). Audit batch ORD:
 *   ORD-9  The server's reasons reach the user: a duplicate order number (409) takes the
 *          wizard back to Basic Info with the message under Order Number; validation
 *          errors (400 `errors[]`) are listed on Review. No generic "please try again".
 *   ORD-12 Only roles that may manage orders get the wizard.
 */

import { Fragment, useState, useEffect, useCallback, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Package,
  MapPin,
  ShoppingCart,
  FileText,
  X,
  Edit2,
  Loader,
  Lock,
} from 'lucide-react';
import useTabDirty from '../../../../hooks/useTabDirty';
import orderService from '../../../../services/orderService';
import ThemedSelect from '../../../common/ThemedSelect';
import { useAuth } from '../../../../types/commonComponents';
import { InlineAlert } from '../shared';
import { MAX_ORDER_SIZE, orderPath, ORDERS_MANAGE } from '../shared/orderHelpers';
import type {
  ApiErrorLike,
  CreateOrderPayload,
  ProductRow,
} from '../../../../types/clientPages/orderManagement';

const MONO = { fontFamily: 'IBM Plex Mono, monospace' };
const SANS = { fontFamily: 'DM Sans, sans-serif' };
const LABEL = 'block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5';
const fieldClass = (hasError: boolean, extra = '') =>
  `w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm ${extra} ${hasError ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`;

interface WizardLineItem {
  product_id: string;
  quantity: string;
  product_code: string;
  product_name: string;
}

interface WizardData {
  order_no: string;
  order_size: string;
  priority: string;
  order_type: string;
  dispatch_location_name: string;
  receipt_location_name: string;
  lineItems: WizardLineItem[];
}

type WizardField = Exclude<keyof WizardData, 'lineItems'>;
type FieldErrors = Partial<Record<WizardField, string>>;
type ChangeHandler = <K extends keyof WizardData>(field: K, value: WizardData[K]) => void;

const ErrorText = ({ text }: { text?: string }) =>
  text ? <p className="text-red-500 text-xs mt-1 font-medium">{text}</p> : null;

/* ─── Progress Stepper ────────────────────── */
interface Step {
  label: string;
  icon: ComponentType<{ className?: string }>;
}

const ProgressStepper = ({ currentStep, steps }: { currentStep: number; steps: Step[] }) => (
  <div className="mb-8">
    <div className="flex items-center justify-between">
      {steps.map((step, index) => {
        const stepNum = index + 1;
        const isCompleted = stepNum < currentStep;
        const isCurrent = stepNum === currentStep;
        const StepIcon = step.icon;

        return (
          <Fragment key={stepNum}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                  isCompleted
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : isCurrent
                      ? 'bg-primary-500 text-white ring-4 ring-primary-100 shadow-sm'
                      : 'bg-slate-100 text-slate-400'
                }`}
              >
                {isCompleted ? <Check className="w-5 h-5" /> : <StepIcon className="w-4 h-4" />}
              </div>
              <span
                className={`text-[11px] mt-2 font-semibold ${isCurrent ? 'text-primary-600' : isCompleted ? 'text-emerald-600' : 'text-slate-400'}`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-3 rounded-full transition-all duration-500 ${isCompleted ? 'bg-emerald-400' : 'bg-slate-200'}`}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  </div>
);

/* ─── Preview Panel ───────────────────────── */
type PreviewField =
  { divider: true } | { divider?: false; label: string; value: string | null; mono?: boolean };

const PreviewPanel = ({ orderData }: { orderData: WizardData }) => {
  const fields: PreviewField[] = [
    { label: 'Order Number', value: orderData.order_no, mono: true },
    {
      label: 'Size',
      value: orderData.order_size ? `${orderData.order_size} MT` : null,
      mono: true,
    },
    { label: 'Priority', value: orderData.priority },
    { label: 'Type', value: orderData.order_type },
    { divider: true },
    { label: 'Dispatch Location', value: orderData.dispatch_location_name },
    { label: 'Receipt Location', value: orderData.receipt_location_name },
  ];

  return (
    <div className="bg-surface rounded-xl border border-slate-200 p-5 sticky top-6">
      <h3 className="text-sm font-bold text-slate-800 mb-4" style={SANS}>
        Order Preview
      </h3>
      <div className="space-y-2.5 text-sm">
        {fields.map((f, i) => {
          if (f.divider) return <div key={i} className="border-t border-slate-100 my-1" />;
          return (
            <div key={i}>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                {f.label}
              </span>
              <p
                className={`font-semibold ${f.value ? 'text-slate-800' : 'text-slate-300'} ${f.mono ? 'font-mono' : ''}`}
                style={f.mono ? MONO : {}}
              >
                {f.value || 'Not set'}
              </p>
            </div>
          );
        })}
        {orderData.lineItems.length > 0 && (
          <>
            <div className="border-t border-slate-100 my-1" />
            <div>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                Line Items
              </span>
              <p className="font-semibold text-slate-800">{orderData.lineItems.length} items</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── Step 1: Basic Info ──────────────────── */
interface StepProps {
  orderData: WizardData;
  errors: FieldErrors;
  onChange: ChangeHandler;
}

const Step1BasicInfo = ({ orderData, errors, onChange }: StepProps) => (
  <div className="space-y-5">
    <div>
      <label htmlFor="order-no" className={LABEL}>
        Order Number <span className="text-red-400">*</span>
      </label>
      <input
        id="order-no"
        type="text"
        maxLength={50}
        value={orderData.order_no}
        onChange={(e) => onChange('order_no', e.target.value)}
        className={fieldClass(!!errors.order_no)}
        placeholder="e.g., ORD-2024-001"
        aria-invalid={!!errors.order_no}
      />
      <ErrorText text={errors.order_no} />
      <p className="text-slate-400 text-xs mt-1">
        Use the order number your weighbridge tickets carry: deliveries are counted by it.
      </p>
    </div>

    <div>
      <label htmlFor="order-size" className={LABEL}>
        Order Size (MT) <span className="text-red-400">*</span>
      </label>
      <input
        id="order-size"
        type="number"
        value={orderData.order_size}
        onChange={(e) => onChange('order_size', e.target.value)}
        className={fieldClass(!!errors.order_size)}
        placeholder="e.g., 1000"
        min="0"
        step="0.01"
      />
      <ErrorText text={errors.order_size} />
    </div>

    <div>
      <span className={LABEL}>
        Priority <span className="text-red-400">*</span>
      </span>
      <ThemedSelect
        value={orderData.priority}
        onChange={(e) => onChange('priority', String(e.target.value))}
        aria-label="Priority"
        className={fieldClass(!!errors.priority, 'bg-surface')}
      >
        <option value="">Select Priority</option>
        <option value="Low">Low</option>
        <option value="Normal">Normal</option>
        <option value="High">High</option>
        <option value="Urgent">Urgent</option>
      </ThemedSelect>
      <ErrorText text={errors.priority} />
    </div>

    <div>
      <span className={LABEL}>
        Order Type <span className="text-red-400">*</span>
      </span>
      <ThemedSelect
        value={orderData.order_type}
        onChange={(e) => onChange('order_type', String(e.target.value))}
        aria-label="Order Type"
        className={fieldClass(!!errors.order_type, 'bg-surface')}
      >
        <option value="">Select Type</option>
        <option value="Dispatch">Dispatch</option>
        <option value="Receipt">Receipt</option>
      </ThemedSelect>
      <ErrorText text={errors.order_type} />
    </div>
  </div>
);

/* ─── Step 2: Locations ───────────────────── */
/* Locations are populated from distinct values in LexiBridgeSource.
   New locations are added via Settings > Location Ownership. */
interface Step2Props extends StepProps {
  dispatchLocations: string[];
  receiptLocations: string[];
  loadingReceipt: boolean;
  onDispatchChange: (name: string) => void;
}

const Step2Locations = ({
  orderData,
  errors,
  dispatchLocations,
  receiptLocations,
  loadingReceipt,
  onChange,
  onDispatchChange,
}: Step2Props) => (
  <div className="space-y-5">
    <div>
      <span className={LABEL}>
        Dispatch Location <span className="text-red-400">*</span>
      </span>
      <ThemedSelect
        value={orderData.dispatch_location_name}
        onChange={(e) => {
          const name = String(e.target.value);
          onChange('dispatch_location_name', name);
          onDispatchChange(name);
        }}
        aria-label="Dispatch Location"
        className={fieldClass(!!errors.dispatch_location_name, 'bg-surface')}
      >
        <option value="">Select Dispatch Location</option>
        {dispatchLocations.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </ThemedSelect>
      <ErrorText text={errors.dispatch_location_name} />
    </div>

    <div>
      <span className={LABEL}>
        Receipt Location <span className="text-red-400">*</span>
      </span>
      <ThemedSelect
        value={orderData.receipt_location_name}
        onChange={(e) => onChange('receipt_location_name', String(e.target.value))}
        aria-label="Receipt Location"
        className={fieldClass(!!errors.receipt_location_name, 'bg-surface')}
        disabled={!orderData.dispatch_location_name || loadingReceipt}
      >
        <option value="">
          {loadingReceipt ? 'Loading receipt locations...' : 'Select Receipt Location'}
        </option>
        {receiptLocations.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </ThemedSelect>
      <ErrorText text={errors.receipt_location_name} />
      {!orderData.dispatch_location_name && (
        <p className="text-slate-400 text-xs mt-1">Select a dispatch location first</p>
      )}
    </div>
  </div>
);

/* ─── Step 3: Products ────────────────────── */
const Step3Products = ({
  orderData,
  products,
  onChange,
}: {
  orderData: WizardData;
  products: ProductRow[];
  onChange: ChangeHandler;
}) => {
  const [lineItems, setLineItems] = useState<WizardLineItem[]>(orderData.lineItems);
  const [currentItem, setCurrentItem] = useState({ product_id: '', quantity: '' });

  const handleAddItem = () => {
    if (!currentItem.product_id || !(Number(currentItem.quantity) > 0)) return;
    const product = products.find((p) => String(p.product_id) === currentItem.product_id);
    if (!product) return;
    const updated = [
      ...lineItems,
      { ...currentItem, product_code: product.product_code, product_name: product.product_name },
    ];
    setLineItems(updated);
    onChange('lineItems', updated);
    setCurrentItem({ product_id: '', quantity: '' });
  };

  const handleRemoveItem = (index: number) => {
    const updated = lineItems.filter((_, i) => i !== index);
    setLineItems(updated);
    onChange('lineItems', updated);
  };

  return (
    <div className="space-y-5">
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
        <p className="text-xs text-primary-700 font-medium">
          Adding products is optional. You can add them now or later on the order's Line Items tab.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <span className={LABEL}>Product</span>
          <ThemedSelect
            value={currentItem.product_id}
            onChange={(e) => setCurrentItem({ ...currentItem, product_id: String(e.target.value) })}
            aria-label="Product"
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm bg-surface"
          >
            <option value="">Select Product</option>
            {products
              .filter((p) => p.active_status)
              .map((p) => (
                <option key={p.product_id} value={String(p.product_id)}>
                  {p.product_code} — {p.product_name}
                </option>
              ))}
          </ThemedSelect>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="item-quantity" className={LABEL}>
              Quantity
            </label>
            <input
              id="item-quantity"
              type="number"
              value={currentItem.quantity}
              onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>
          <button
            onClick={handleAddItem}
            disabled={!currentItem.product_id || !(Number(currentItem.quantity) > 0)}
            className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
          >
            Add
          </button>
        </div>
      </div>

      {lineItems.length > 0 && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Product
                </th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Qty
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-400 uppercase tracking-widest w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lineItems.map((item, index) => (
                <tr key={index} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5 text-sm text-slate-800">
                    {item.product_code} — {item.product_name}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-800 tabular-nums" style={MONO}>
                    {item.quantity}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => handleRemoveItem(index)}
                      className="p-1 rounded hover:bg-red-50 text-red-500 transition-colors"
                      aria-label={`Remove ${item.product_code}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ─── Step 4: Review ──────────────────────── */
const Step4Review = ({
  orderData,
  submitErrors,
  onEdit,
}: {
  orderData: WizardData;
  submitErrors: string[];
  onEdit: (step: number) => void;
}) => {
  const sections = [
    {
      title: 'Basic Information',
      step: 1,
      fields: [
        { label: 'Order Number', value: orderData.order_no },
        { label: 'Size', value: `${orderData.order_size} MT` },
        { label: 'Priority', value: orderData.priority },
        { label: 'Type', value: orderData.order_type },
      ],
    },
    {
      title: 'Locations',
      step: 2,
      fields: [
        { label: 'Dispatch', value: orderData.dispatch_location_name },
        { label: 'Receipt', value: orderData.receipt_location_name },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      {submitErrors.length > 0 && (
        <InlineAlert>
          <p className="font-semibold">The order was not created:</p>
          <ul className="list-disc ml-4 mt-1">
            {submitErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </InlineAlert>
      )}

      {sections.map((section) => (
        <div key={section.step} className="bg-surface rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-800" style={SANS}>
              {section.title}
            </h3>
            <button
              onClick={() => onEdit(section.step)}
              className="p-1 rounded hover:bg-primary-50 text-primary-600 transition-colors"
              aria-label={`Edit ${section.title}`}
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {section.fields.map((f) => (
              <div key={f.label}>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  {f.label}
                </span>
                <p className="font-semibold text-slate-800">{f.value}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {orderData.lineItems.length > 0 && (
        <div className="bg-surface rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-800" style={SANS}>
              Line Items ({orderData.lineItems.length})
            </h3>
            <button
              onClick={() => onEdit(3)}
              className="p-1 rounded hover:bg-primary-50 text-primary-600 transition-colors"
              aria-label="Edit Line Items"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-1.5 text-sm">
            {orderData.lineItems.map((item, i) => (
              <div
                key={i}
                className="flex justify-between py-1.5 border-b border-slate-100 last:border-0"
              >
                <span className="text-slate-800">{item.product_name}</span>
                <span className="font-semibold text-slate-800 tabular-nums" style={MONO}>
                  {item.quantity}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const STEPS: Step[] = [
  { label: 'Basic Info', icon: Package },
  { label: 'Locations', icon: MapPin },
  { label: 'Products', icon: ShoppingCart },
  { label: 'Review', icon: FileText },
];

const EMPTY_ORDER: WizardData = {
  order_no: '',
  order_size: '',
  priority: '',
  order_type: '',
  dispatch_location_name: '',
  receipt_location_name: '',
  lineItems: [],
};

const DUPLICATE_ORDER_NO = /order number already exists/i;

/* ═══════════════════════════════════════════════
   MAIN CREATE ORDER COMPONENT
   ═══════════════════════════════════════════════ */
const CreateOrder = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(ORDERS_MANAGE);
  const [currentStep, setCurrentStep] = useState(1);
  const [orderData, setOrderData] = useState<WizardData>(EMPTY_ORDER);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);
  const [dispatchLocations, setDispatchLocations] = useState<string[]>([]);
  const [receiptLocations, setReceiptLocations] = useState<string[]>([]);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(false);

  // A part-filled order exists only in this pane, so closing its tab would throw
  // the work away with no undo.
  const hasEnteredSomething =
    currentStep > 1 ||
    orderData.lineItems.length > 0 ||
    Object.entries(orderData).some(([k, v]) => k !== 'lineItems' && v !== '');
  useTabDirty(hasEnteredSomething, 'This order has not been created yet.');

  useEffect(() => {
    if (!canManage) return;
    const loadData = async () => {
      try {
        const [dispatchLocs, prodData] = await Promise.all([
          orderService.getSourceDispatchLocations(),
          orderService.getProducts({ active: true }),
        ]);
        setDispatchLocations(dispatchLocs || []);
        setProducts(prodData.products || []);
      } catch (error) {
        console.error('Failed to load dropdown data:', error);
      }
    };
    loadData();
  }, [canManage]);

  const handleDispatchLocationChange = useCallback(async (dispatchName: string) => {
    // Reset receipt location when dispatch changes
    setOrderData((prev) => ({ ...prev, receipt_location_name: '' }));
    if (dispatchName) {
      try {
        setLoadingReceipt(true);
        const receiptLocs = await orderService.getSourceReceiptLocations(dispatchName);
        setReceiptLocations((receiptLocs || []).filter((name) => name !== dispatchName));
      } catch {
        setReceiptLocations([]);
      } finally {
        setLoadingReceipt(false);
      }
    } else {
      setReceiptLocations([]);
    }
  }, []);

  const handleChange: ChangeHandler = (field, value) => {
    setOrderData((prev) => ({ ...prev, [field]: value }));
    if (field !== 'lineItems') setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateStep = () => {
    const e: FieldErrors = {};
    if (currentStep === 1) {
      const size = Number(orderData.order_size);
      if (!orderData.order_no.trim()) e.order_no = 'Order number is required';
      if (!orderData.order_size || !(size > 0)) e.order_size = 'Valid order size is required';
      else if (size > MAX_ORDER_SIZE)
        e.order_size = 'Order size is too large (max: 999,999.999 MT)';
      if (!orderData.priority) e.priority = 'Priority is required';
      if (!orderData.order_type) e.order_type = 'Order type is required';
    } else if (currentStep === 2) {
      if (!orderData.dispatch_location_name)
        e.dispatch_location_name = 'Dispatch location is required';
      if (!orderData.receipt_location_name)
        e.receipt_location_name = 'Receipt location is required';
      if (
        orderData.dispatch_location_name &&
        orderData.dispatch_location_name === orderData.receipt_location_name
      )
        e.receipt_location_name = 'Must differ from dispatch';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) setCurrentStep((p) => Math.min(p + 1, STEPS.length));
  };
  const handlePrevious = () => setCurrentStep((p) => Math.max(p - 1, 1));

  const handleSubmit = async () => {
    setSubmitErrors([]);
    try {
      setLoading(true);
      // Map wizard state to the keys the server destructures (priority/lineItems are renamed)
      const payload: CreateOrderPayload = {
        order_no: orderData.order_no.trim(),
        order_size: orderData.order_size,
        order_priority: orderData.priority,
        order_type: orderData.order_type,
        dispatch_location_name: orderData.dispatch_location_name,
        receipt_location_name: orderData.receipt_location_name,
        line_items: orderData.lineItems.map((i) => ({
          product_id: Number(i.product_id),
          quantity: i.quantity,
        })),
      };
      const created = await orderService.createOrder(payload);
      // API wraps the created order: { success, order }
      navigate(orderPath(created.order?.order_no ?? payload.order_no));
    } catch (error) {
      console.error('Failed to create order:', error);
      const e = error as ApiErrorLike;
      const data = e.response?.data;
      const reasons =
        Array.isArray(data?.errors) && data.errors.length > 0
          ? data.errors
          : [
              e.response?.status === 403
                ? 'Your role does not allow creating orders.'
                : data?.error || data?.message || e.message || 'The server did not answer.',
            ];
      const duplicate = reasons.find((r) => DUPLICATE_ORDER_NO.test(r));
      if (duplicate || e.response?.status === 409) {
        // ORD-9: the order number is the field to fix — take the user to it.
        setErrors({ order_no: duplicate || reasons[0] });
        setSubmitErrors(reasons.filter((r) => r !== duplicate));
        setCurrentStep(1);
      } else {
        setSubmitErrors(reasons);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!canManage) {
    return (
      <div className="p-6 lg:p-8">
        <div className="bg-surface rounded-xl border border-slate-200 p-8 text-center max-w-md mx-auto">
          <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm font-semibold text-slate-800">
            Your role does not allow creating orders.
          </p>
          <p className="text-xs text-slate-500 mt-1 mb-4">Ask an administrator for access.</p>
          <button
            onClick={() => navigate('/order-management/orders')}
            className="px-4 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900" style={SANS}>
            Create New Order
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Complete the steps below to create a new order
          </p>
        </div>

        <ProgressStepper currentStep={currentStep} steps={STEPS} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-surface rounded-xl border border-slate-200 p-6">
              {currentStep === 1 && (
                <Step1BasicInfo orderData={orderData} errors={errors} onChange={handleChange} />
              )}
              {currentStep === 2 && (
                <Step2Locations
                  orderData={orderData}
                  errors={errors}
                  dispatchLocations={dispatchLocations}
                  receiptLocations={receiptLocations}
                  loadingReceipt={loadingReceipt}
                  onChange={handleChange}
                  onDispatchChange={handleDispatchLocationChange}
                />
              )}
              {currentStep === 3 && (
                <Step3Products orderData={orderData} products={products} onChange={handleChange} />
              )}
              {currentStep === 4 && (
                <Step4Review
                  orderData={orderData}
                  submitErrors={submitErrors}
                  onEdit={setCurrentStep}
                />
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-5 border-t border-slate-100">
                <button
                  onClick={
                    currentStep === 1 ? () => navigate('/order-management/orders') : handlePrevious
                  }
                  className="px-4 py-2 text-slate-600 hover:text-slate-900 font-semibold transition-colors flex items-center text-sm"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {currentStep === 1 ? 'Cancel' : 'Back'}
                </button>
                {currentStep < STEPS.length ? (
                  <button
                    onClick={handleNext}
                    className="px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-semibold flex items-center text-sm shadow-sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-5 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold flex items-center text-sm shadow-sm"
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" /> Creating...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" /> Create Order
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:col-span-1">
            <PreviewPanel orderData={orderData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrder;
